import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Bell, CheckCircle, Calendar, ThumbsUp, Award, UserCheck, 
  XCircle, MessageSquare, ChevronRight, Check, Clock, RefreshCw
} from 'lucide-react';

const NotificationPanel = ({ fetchNotifications, markRead, markAllRead, title = "HR Activity" }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [newNotifAnimation, setNewNotifAnimation] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [isPolling, setIsPolling] = useState(true);
  
  const pollingIntervalRef = useRef(null);
  const previousUnreadCount = useRef(0);

  // Poll for new notifications every 10 seconds
  useEffect(() => {
    const startPolling = () => {
      pollingIntervalRef.current = setInterval(async () => {
        if (isPolling) {
          try {
            const response = await fetchNotifications(10, 0);
            const newNotifs = response.data || [];
            const newUnread = response.unreadCount || 0;
            
            // Check if there are new notifications
            if (newUnread > previousUnreadCount.current) {
              // Trigger animation for new notification
              setNewNotifAnimation(true);
              setTimeout(() => setNewNotifAnimation(false), 1000);
              
              // Play notification sound (optional)
              try {
                const audio = new Audio('/notification.mp3');
                audio.volume = 0.3;
                audio.play().catch(() => {});
              } catch (e) {}
            }
            
            previousUnreadCount.current = newUnread;
            setNotifications(newNotifs);
            setUnreadCount(newUnread);
            setLastRefresh(Date.now());
          } catch (error) {
            console.error('Error polling notifications:', error);
          }
        }
      }, 10000); // Poll every 10 seconds
    };

    startPolling();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchNotifications, isPolling]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await fetchNotifications(10, 0);
      const notifs = response.data || [];
      setNotifications(notifs);
      setUnreadCount(response.unreadCount || 0);
      previousUnreadCount.current = response.unreadCount || 0;
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadNotifications();
  };

  const handleMarkRead = async (notifId) => {
    try {
      await markRead(notifId);
      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      previousUnreadCount.current = Math.max(0, previousUnreadCount.current - 1);
    } catch (error) {
      console.error('Error marking read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      previousUnreadCount.current = 0;
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      shortlisted: <ThumbsUp className="w-4 h-4 text-purple-600" />,
      scheduled: <Calendar className="w-4 h-4 text-yellow-600" />,
      interviewed: <UserCheck className="w-4 h-4 text-orange-600" />,
      feedback: <MessageSquare className="w-4 h-4 text-blue-600" />,
      offer: <Award className="w-4 h-4 text-green-600" />,
      hired: <CheckCircle className="w-4 h-4 text-emerald-600" />,
      rejected: <XCircle className="w-4 h-4 text-red-600" />
    };
    return iconMap[type] || <Bell className="w-4 h-4 text-gray-600" />;
  };

  const getNotificationBg = (type, isRead) => {
    if (isRead) return 'bg-gray-50';
    const bgMap = {
      shortlisted: 'bg-purple-50 border-l-4 border-l-purple-500',
      scheduled: 'bg-yellow-50 border-l-4 border-l-yellow-500',
      interviewed: 'bg-orange-50 border-l-4 border-l-orange-500',
      feedback: 'bg-blue-50 border-l-4 border-l-blue-500',
      offer: 'bg-green-50 border-l-4 border-l-green-500',
      hired: 'bg-emerald-50 border-l-4 border-l-emerald-500',
      rejected: 'bg-red-50 border-l-4 border-l-red-500'
    };
    return bgMap[type] || 'bg-gray-50';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const formatLastRefresh = () => {
    const seconds = Math.floor((Date.now() - lastRefresh) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  if (loading) {
    return (
      <Card className="bg-white border border-gray-200 shadow-sm p-4" data-testid="notification-panel">
        <div className="flex items-center gap-2 text-gray-500">
          <Clock className="w-4 h-4 animate-spin" />
          <span>Loading notifications...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className={`bg-white border border-gray-200 shadow-sm transition-all duration-300 ${newNotifAnimation ? 'ring-2 ring-purple-400 ring-opacity-75' : ''}`} 
      data-testid="notification-panel"
    >
      {/* Header */}
      <div 
        className="p-4 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className={`w-5 h-5 text-purple-600 ${newNotifAnimation ? 'animate-bounce' : ''}`} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {unreadCount > 0 && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
          {/* Auto-refresh indicator */}
          <div className="flex items-center gap-1" title="Auto-refreshing every 10 seconds">
            <RefreshCw className="w-3 h-3 text-green-500" />
            <span className="text-xs text-green-600">Live</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            title="Refresh now"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); handleMarkAllRead(); }}
              className="text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              data-testid="mark-all-read-btn"
            >
              <Check className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>

      {/* Notifications List */}
      {expanded && (
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No notifications yet</p>
              <p className="text-sm mt-1">HR actions will appear here automatically</p>
            </div>
          ) : (
            notifications.map((notif, index) => (
              <div 
                key={notif.id}
                className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-all duration-300 ${getNotificationBg(notif.type, notif.isRead)} ${index === 0 && newNotifAnimation ? 'animate-pulse bg-purple-100' : ''}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getNotificationIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${notif.isRead ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                        {notif.message}
                      </p>
                      {!notif.isRead && (
                        <button 
                          onClick={() => handleMarkRead(notif.id)}
                          className="flex-shrink-0 text-purple-600 hover:text-purple-700"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>by {notif.actionByName}</span>
                      <span>•</span>
                      <span>{notif.companyName}</span>
                      <span>•</span>
                      <span>{formatTime(notif.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
};

export default NotificationPanel;
